import { useApolloClient, useQuery } from "@apollo/client";
import {
  Button,
  Chip,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { documentTitle } from "encamp-shared/src/waste/shippingDocumentLabels";
import { WasteEpisodicEventStatus } from "generated-graphql/graphql";
import { useProcessedManifest } from "hooks/useProcessedManifest";
import { useTenant } from "hooks/useTenant";
import { useWasteLinesUnified } from "hooks/useWasteLinesUnified";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { formatContainerDate } from "../Containers/containerDisplay";
import { AddEventManifestDialog } from "./AddEventManifestDialog";
import {
  EPISODIC_EVENT_MANIFESTS_QUERY,
  EPISODIC_EVENTS_FOR_MANIFEST_QUERY,
  EPISODIC_MANIFEST_CANDIDATES_QUERY,
} from "./api";
import { DeclarationReviewDialog } from "./DeclarationReviewDialog";
import { episodicWindowEnd, EVENT_TYPE_LABELS } from "./episodicDisplay";
import { describeAttachError } from "./episodicErrors";
import { EpisodicEventDialog } from "./EpisodicEventDialog";
import {
  declareDefaultsForManifest,
  ELIGIBILITY_CHECKING,
  ELIGIBILITY_UNCHECKED,
  eligibilityFromCandidates,
  episodicEventsForManifestVariables,
  EventEligibility,
  eventsCoveringShipDate,
  MANIFEST_SIDE_REFETCH_QUERIES,
  ManifestForEpisodic,
  PickableEvent,
  pickableEvents,
  wasteCodesOnLines,
} from "./markManifestEpisodic";

type ChosenEvent = Pick<
  PickableEvent,
  "id" | "startDate" | "reason" | "facilityId" | "reliefApplies" | "declarationsWritable"
>;

const chosen = (event: ChosenEvent): ChosenEvent => ({
  id: event.id,
  startDate: event.startDate,
  reason: event.reason,
  facilityId: event.facilityId,
  reliefApplies: event.reliefApplies,
  declarationsWritable: event.declarationsWritable,
});

type DeclareDefaults = ReturnType<typeof declareDefaultsForManifest>;

const markEpisodicTitle = (manifest: ManifestForEpisodic) =>
  `Mark ${documentTitle(manifest)} as episodic`;

type Step =
  | { kind: "pick" }
  | { kind: "declare"; defaults: DeclareDefaults }
  | { kind: "amounts"; event: ChosenEvent; from: "pick" | "declare" | "declared" }
  | { kind: "review"; event: ChosenEvent };

const DECLARE_NEW = "new";

const eventPagePath = (tenantId: string | undefined, facilityId: string) =>
  `/o/${tenantId}/waste/episodic-events?facilityId=${facilityId}`;

function TracksContainersNote({ eventPath }: { eventPath: string }) {
  return (
    <Typography variant="caption" color="text.secondary">
      This event counts its waste from containers. Attach the container on the{" "}
      <Link component={RouterLink} to={eventPath}>
        event page
      </Link>{" "}
      instead.
    </Typography>
  );
}

function EventOption({
  event,
  eligibility,
  eventPath,
}: {
  event: PickableEvent;
  eligibility: EventEligibility;
  eventPath: string;
}) {
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" fontWeight={600}>
        {event.reason}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary">
          {EVENT_TYPE_LABELS[event.type]} · {formatContainerDate(event.startDate)} to{" "}
          {formatContainerDate(episodicWindowEnd(event.startDate) ?? event.endDate)} ·{" "}
          {event.facilityName}
        </Typography>
        {event.status === WasteEpisodicEventStatus.Finalized && (
          <Chip size="small" label="Finalized" />
        )}
        {!event.declarationsWritable && <Chip size="small" label="Tracks containers" />}
      </Stack>
      {!event.declarationsWritable && <TracksContainersNote eventPath={eventPath} />}
      {event.declarationsWritable && eligibility.kind === "checking" && (
        <Typography variant="caption" color="text.secondary">
          Checking this manifest against the event…
        </Typography>
      )}
      {event.declarationsWritable && eligibility.kind === "ineligible" && (
        <Typography variant="caption" color="warning.main">
          {eligibility.reason}
        </Typography>
      )}
    </Stack>
  );
}

function OptionRow({
  value,
  label,
  disabled = false,
}: {
  value: string;
  label: ReactNode;
  disabled?: boolean;
}) {
  return (
    <FormControlLabel
      value={value}
      control={<Radio size="small" sx={{ p: 0, mr: 1 }} />}
      disabled={disabled}
      // Top-aligned so the radio sits on the first line of a two-line option;
      // a padless small radio is exactly one body2 line tall.
      sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
      label={label}
    />
  );
}

/** Asks the server, per event, whether this manifest is a candidate inside the event's window. */
function useEventEligibility(
  events: PickableEvent[],
  manifestTrackingNumber: string
): Record<string, EventEligibility> {
  const client = useApolloClient();
  const [checked, setChecked] = useState<Record<string, EventEligibility>>({});
  const ids = events.map((event) => event.id).join(",");

  useEffect(() => {
    let cancelled = false;
    for (const id of ids.split(",").filter((value) => value !== "")) {
      void client
        .query({
          query: EPISODIC_MANIFEST_CANDIDATES_QUERY,
          variables: { id, search: manifestTrackingNumber },
        })
        .then(
          ({ data }) =>
            eligibilityFromCandidates(
              data.episodicEvent?.manifestCandidates ?? [],
              manifestTrackingNumber
            ),
          () => ELIGIBILITY_UNCHECKED
        )
        .then((result) => {
          if (!cancelled) {
            setChecked((prev) => ({ ...prev, [id]: result }));
          }
        });
    }
    return () => {
      cancelled = true;
    };
  }, [client, ids, manifestTrackingNumber]);

  return checked;
}

function useEventsForManifest(manifest: ManifestForEpisodic, tenantId: string | undefined) {
  const { error: alertError } = useAlerts();
  const { data, loading, error } = useQuery(EPISODIC_EVENTS_FOR_MANIFEST_QUERY, {
    variables: episodicEventsForManifestVariables(manifest.facilityId, tenantId ?? ""),
    skip: !tenantId,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (error) {
      alertError(describeAttachError(error, "The episodic events could not be loaded"));
    }
  }, [error, alertError]);

  const covering = useMemo(
    () => (data ? eventsCoveringShipDate(pickableEvents(data), manifest.shippedDate) : []),
    [data, manifest.shippedDate]
  );
  return {
    covering,
    facilityName: data?.facility?.name ?? "this facility",
    loading: loading || data === undefined,
  };
}

function NothingToDeclareDialog({
  manifest,
  onClose,
}: {
  manifest: ManifestForEpisodic;
  onClose: () => void;
}) {
  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={markEpisodicTitle(manifest)}
      noPrimaryAction
      additionalPrimaryActionElements={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <Typography variant="body2" color="text.secondary">
        None of the waste on this manifest counts toward generator status, so an episodic
        declaration would change nothing.
      </Typography>
    </Dialog>
  );
}

function PickEventStep({
  manifest,
  tenantId,
  declaredEventId,
  onPicked,
  onDeclared,
  onDeclare,
  onClose,
}: {
  manifest: ManifestForEpisodic;
  tenantId: string | undefined;
  /** A manifest belongs to at most one event; when it already does, the pick step is skipped. */
  declaredEventId?: string;
  onPicked: (event: ChosenEvent) => void;
  onDeclared: (event: ChosenEvent) => void;
  onDeclare: () => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  const { covering, facilityName, loading } = useEventsForManifest(manifest, tenantId);
  // The server refuses candidates on an event that tracks containers, so only writable ones are asked.
  const writable = useMemo(() => covering.filter((e) => e.declarationsWritable), [covering]);
  const checked = useEventEligibility(writable, manifest.manifestTrackingNumber);
  const declared = declaredEventId
    ? (covering.find((e) => e.id === declaredEventId) ?? null)
    : null;
  useEffect(() => {
    if (declared) {
      onDeclared(chosen(declared));
    }
  }, [declared, onDeclared]);
  if (declaredEventId && (declared || loading)) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth title="Edit episodic pounds">
        <Typography variant="caption" color="text.secondary">
          Loading the event…
        </Typography>
      </Dialog>
    );
  }
  const eligibilityOf = (id: string): EventEligibility => checked[id] ?? ELIGIBILITY_CHECKING;
  const canProceed =
    selected === DECLARE_NEW || (selected !== null && eligibilityOf(selected).kind === "eligible");

  const handleNext = () => {
    if (selected === DECLARE_NEW) {
      onDeclare();
      return;
    }
    const event = covering.find((e) => e.id === selected);
    if (event && eligibilityOf(event.id).kind === "eligible") {
      onPicked(chosen(event));
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={markEpisodicTitle(manifest)}
      primaryActionButtonProps={{ saveText: "Next", onClick: handleNext, disabled: !canProceed }}
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Shipped {formatContainerDate(manifest.shippedDate)} · choose the episodic event this waste
          came from.
        </Typography>
        {loading && covering.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Loading events…
          </Typography>
        )}
        {!loading && covering.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No episodic event at {facilityName} covers this ship date.
          </Typography>
        )}
        {covering.length > 0 && writable.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Every event at {facilityName} that covers this ship date counts its waste from
            containers. Attach the container on the{" "}
            <Link component={RouterLink} to={eventPagePath(tenantId, manifest.facilityId)}>
              event page
            </Link>{" "}
            instead, or declare a new event for these pounds.
          </Typography>
        )}
        <RadioGroup value={selected ?? ""} onChange={(_e, value) => setSelected(value)}>
          <Stack spacing={1.5}>
            {covering.map((event) => (
              <OptionRow
                key={event.id}
                value={event.id}
                disabled={
                  !event.declarationsWritable || eligibilityOf(event.id).kind !== "eligible"
                }
                label={
                  <EventOption
                    event={event}
                    eligibility={eligibilityOf(event.id)}
                    eventPath={eventPagePath(tenantId, event.facilityId)}
                  />
                }
              />
            ))}
            <OptionRow
              value={DECLARE_NEW}
              label={
                <Typography variant="body2" fontWeight={600}>
                  Declare a new episodic event
                </Typography>
              }
            />
          </Stack>
        </RadioGroup>
      </Stack>
    </Dialog>
  );
}

function EventAmountsStep({
  manifest,
  event,
  tenantId,
  onBack,
  onClose,
}: {
  manifest: ManifestForEpisodic;
  event: ChosenEvent;
  tenantId: string;
  onBack: (() => void) | undefined;
  onClose: () => void;
}) {
  const { data } = useQuery(EPISODIC_EVENT_MANIFESTS_QUERY, { variables: { id: event.id } });
  const exemptions = data?.episodicEvent?.manifestExemptions;
  const title = markEpisodicTitle(manifest);

  if (!exemptions) {
    return (
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth title={title}>
        <Typography variant="body2" color="text.secondary">
          Loading the event…
        </Typography>
      </Dialog>
    );
  }

  const existingRow = exemptions.manifests.some(
    (m) => m.manifestTrackingNumber === manifest.manifestTrackingNumber
  );
  return (
    <AddEventManifestDialog
      eventId={event.id}
      startDate={event.startDate}
      exemptions={exemptions}
      reliefApplies={event.reliefApplies}
      initialManifestTrackingNumber={manifest.manifestTrackingNumber}
      eventLink={{
        href: eventPagePath(tenantId, event.facilityId),
        reason: event.reason,
      }}
      title={existingRow ? "Edit episodic pounds" : title}
      onBack={onBack}
      extraRefetchQueries={MANIFEST_SIDE_REFETCH_QUERIES}
      open
      onClose={onClose}
    />
  );
}

function DeclarationReviewStep({
  manifestTrackingNumber,
  event,
  onClose,
}: {
  manifestTrackingNumber: string;
  event: ChosenEvent;
  onClose: () => void;
}) {
  const { data, error } = useQuery(EPISODIC_EVENT_MANIFESTS_QUERY, { variables: { id: event.id } });
  const exemptions = data?.episodicEvent?.manifestExemptions;
  const manifest = exemptions?.manifests.find(
    (m) => m.manifestTrackingNumber === manifestTrackingNumber
  );
  if (!manifest) {
    return (
      <Dialog
        open
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        title="Episodic pounds"
        noPrimaryAction
        additionalPrimaryActionElements={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
        ]}
      >
        <Typography variant="body2" color="text.secondary">
          {exemptions
            ? "No pounds are entered on this manifest."
            : error
              ? "The manifests could not be loaded."
              : "Loading the event…"}
        </Typography>
      </Dialog>
    );
  }
  return (
    <DeclarationReviewDialog
      eventReason={event.reason}
      manifest={manifest}
      extraRefetchQueries={MANIFEST_SIDE_REFETCH_QUERIES}
      onClose={onClose}
    />
  );
}

export function MarkManifestEpisodicDialog({
  manifest,
  tenantIdOverride,
  declaredEventId,
  open,
  onClose,
}: {
  manifest: ManifestForEpisodic;
  tenantIdOverride?: string;
  /** The event this manifest is already declared on, if any; skips the pick step. */
  declaredEventId?: string;
  open: boolean;
  onClose: () => void;
}) {
  if (!open) {
    return null;
  }
  return (
    <MarkManifestEpisodicFlow
      key={manifest.processedManifestId}
      manifest={manifest}
      tenantIdOverride={tenantIdOverride}
      declaredEventId={declaredEventId}
      onClose={onClose}
    />
  );
}

function MarkManifestEpisodicFlow({
  manifest,
  tenantIdOverride,
  declaredEventId,
  onClose,
}: {
  manifest: ManifestForEpisodic;
  tenantIdOverride?: string;
  declaredEventId?: string;
  onClose: () => void;
}) {
  const { tenantId: routeTenantId } = useTenant();
  const tenantId = tenantIdOverride ?? routeTenantId;
  const [step, setStep] = useState<Step>({ kind: "pick" });
  // The server says whether any line counts toward status; a manifest with none
  // has nothing to declare, and a new event for it would only use up a slot.
  const { processedManifest } = useProcessedManifest(manifest.processedManifestId, tenantId);
  // The lines feed the waste codes a new event is prefilled with; which events
  // the manifest qualifies at is the server's answer, asked per event.
  const { items: lines } = useWasteLinesUnified(
    {
      search: `tenantId:${tenantId} processedManifestId:${manifest.processedManifestId}`,
      pageSize: 500,
    },
    { tenantId }
  );

  if (processedManifest?.episodicDeclarable === false && !declaredEventId) {
    return <NothingToDeclareDialog manifest={manifest} onClose={onClose} />;
  }

  switch (step.kind) {
    case "pick":
      return (
        <PickEventStep
          manifest={manifest}
          tenantId={tenantId}
          declaredEventId={declaredEventId}
          onPicked={(event) => setStep({ kind: "amounts", event, from: "pick" })}
          onDeclared={(event) =>
            setStep(
              event.declarationsWritable
                ? { kind: "amounts", event, from: "declared" }
                : { kind: "review", event }
            )
          }
          onDeclare={() =>
            setStep({
              kind: "declare",
              defaults: declareDefaultsForManifest(manifest, wasteCodesOnLines(lines)),
            })
          }
          onClose={onClose}
        />
      );
    case "declare":
      return (
        <EpisodicEventDialog
          open
          mode="declare"
          facilityId={manifest.facilityId}
          initialValues={step.defaults}
          onClose={() => setStep({ kind: "pick" })}
          onCreated={(event) =>
            setStep({
              kind: "amounts",
              event: {
                id: event.id,
                startDate: event.startDate,
                reason: event.reason,
                facilityId: event.facilityId,
                reliefApplies: event.relief.isAvailable,
                declarationsWritable: event.declarationsWritable,
              },
              from: "declare",
            })
          }
        />
      );
    case "amounts":
      return (
        <EventAmountsStep
          tenantId={tenantId ?? ""}
          manifest={manifest}
          event={step.event}
          onBack={step.from === "pick" ? () => setStep({ kind: "pick" }) : undefined}
          onClose={onClose}
        />
      );
    case "review":
      return (
        <DeclarationReviewStep
          manifestTrackingNumber={manifest.manifestTrackingNumber}
          event={step.event}
          onClose={onClose}
        />
      );
  }
}
